import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { getDatabase } from "firebase/database";
import moment from "moment";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const database = getDatabase(app);
export const analyticsObj = getAnalytics(app);

export const getDates = async (plant) => {
  const dbRef = collection(db, "microgreens", plant, "harvestData");

  let startDate = Date.now() - 5184000000;
  let seedStartDate = Date.now() - 1036800000;

  let tempArr = [];
  const q = query(
    dbRef,
    where("seed", "<", seedStartDate),
    where("seed", ">", startDate)
  );
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    // doc.data() is never undefined for query doc snapshots
    tempArr.push(doc.data());
  });
  return tempArr;
};

export const getPlant = async (plant, seedDate, harvestDate) => {
  let tempPlant;
  let darkPh = [];
  let darkPhDates = [];
  let lightPh = [];
  let lightPhDates = [];
  let tempEc = [];
  let tempEcDates = [];
  let tempDarkEc = [];
  let tempDarkEcDates = [];
  let tempDark = [];
  let tempDarkDates = [];
  let tempLight = [];
  let tempLightDates = [];
  let tempDarkWater = [];
  let tempDarkWaterDates = [];
  let tempLightWater = [];
  let tempLightWaterDates = [];
  let humDark = [];
  let humDarkDates = [];
  let humLight = [];
  let humLightDates = [];
  let coolTemp;

  const dbRef = doc(db, "microgreens", plant, "harvestData", seedDate);
  const docSnap = await getDoc(dbRef);
  if (docSnap.exists()) {
    tempPlant = docSnap.data();

    // Light pH
    const phRef = collection(
      db,
      "sensors",
      tempPlant.ph.toString(),
      "sensorData"
    );
    const phQ = query(
      phRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(harvestDate))
    );
    const phSnapshot = await getDocs(phQ);
    phSnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      lightPhDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      lightPh.push(doc.data().value);
    });

    //darkPh
    const phDarkRef = collection(db, "sensors", "ph1", "sensorData");
    const phDarkQ = query(
      phDarkRef,
      where("time", ">", Number(tempPlant.seed)),
      where("time", "<", Number(tempPlant.lightActive))
    );
    const phDarkSnapshot = await getDocs(phDarkQ);
    phDarkSnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      darkPhDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      darkPh.push(doc.data().value);
    });

    // EC Light
    const ecRef = collection(
      db,
      "sensors",
      tempPlant.ec.toString(),
      "sensorData"
    );
    const ecQ = query(
      ecRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(harvestDate))
    );
    const ecSnapshot = await getDocs(ecQ);
    ecSnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      tempEcDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempEc.push(doc.data().value);
    });

    // EC Dark
    const ecDarkRef = collection(db, "sensors", "ec1", "sensorData");
    const ecDarkQ = query(
      ecDarkRef,
      where("time", ">", Number(tempPlant.seed)),
      where("time", "<", Number(tempPlant.lightActive))
    );
    const ecDarkSnapshot = await getDocs(ecDarkQ);
    ecDarkSnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      tempDarkEcDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempDarkEc.push(doc.data().value / 2);
    });

    // Temp Light
    const tempRef = collection(
      db,
      "sensors",
      tempPlant.temp.toString(),
      "sensorData"
    );
    const tempQ = query(
      tempRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(harvestDate))
    );
    const tempSnapshot = await getDocs(tempQ);
    tempSnapshot.forEach((doc) => {
      tempLightDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempLight.push(doc.data().value);
    });

    // Temp Dark
    const tempDarkRef = collection(db, "sensors", "temp1", "sensorData");
    const tempDarkQ = query(
      tempDarkRef,
      where("time", ">", Number(tempPlant.seed)),
      where("time", "<", Number(tempPlant.lightActive))
    );
    const tempDarkSnapshot = await getDocs(tempDarkQ);
    tempDarkSnapshot.forEach((doc) => {
      tempDarkDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempDark.push(doc.data().value * 0.85);
    });

    // Hum Light
    const humRef = collection(
      db,
      "sensors",
      tempPlant.hum.toString(),
      "sensorData"
    );
    const humQ = query(
      humRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(harvestDate))
    );
    const humSnapshot = await getDocs(humQ);
    humSnapshot.forEach((doc) => {
      humLightDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      humLight.push(doc.data().value);
    });

    // Hum Dark
    const humDarkRef = collection(db, "sensors", "hum3", "sensorData");
    const humDarkQ = query(
      humDarkRef,
      where("time", ">", Number(tempPlant.seed)),
      where("time", "<", Number(tempPlant.lightActive))
    );
    const humDarkSnapshot = await getDocs(humDarkQ);
    humDarkSnapshot.forEach((doc) => {
      humDarkDates.push(moment(doc.data().time).format("MM-DD HH:mm"));
      humDark.push(doc.data().value);
    });

    // Water Light
    const waterRef = collection(db, "sensors", "waterTemp1", "sensorData");
    const waterQ = query(
      waterRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(harvestDate))
    );
    const waterSnapshot = await getDocs(waterQ);
    waterSnapshot.forEach((doc) => {
      tempLightWater.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempLightWaterDates.push(doc.data().value);
    });

    // Water Dark
    const waterDarkRef = collection(db, "sensors", "waterTemp2", "sensorData");
    const waterDarkQ = query(
      waterDarkRef,
      where("time", ">", Number(tempPlant.seed)),
      where("time", "<", Number(tempPlant.lightActive))
    );
    const waterDarkSnapshot = await getDocs(waterDarkQ);
    waterDarkSnapshot.forEach((doc) => {
      tempDarkWater.push(moment(doc.data().time).format("MM-DD HH:mm"));
      tempDarkWaterDates.push(doc.data().value * 0.9);
    });

    // Temp Cooler
    let coolSum = 0;
    let coolCount = 0;
    const coolRef = collection(db, "sensors", "cool1", "sensorData");
    const coolQ = query(
      coolRef,
      where("time", ">", Number(tempPlant.lightActive)),
      where("time", "<", Number(tempPlant.delivery))
    );
    const coolSnapshot = await getDocs(coolQ);
    coolSnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      coolSum += doc.data().value;
      coolCount += 1;
    });

    coolTemp = coolSum / coolCount;

    return {
      data: tempPlant,
      darkPh: darkPh,
      darkPhDates: darkPhDates,
      ec: tempEc,
      ecDates: tempEcDates,
      darkEc: tempDarkEc,
      darkEcDates: tempDarkEcDates,
      lightPh: lightPh,
      lightPhDates: lightPhDates,
      tempDark: tempDark,
      tempLight: tempLight,
      tempDarkDates: tempDarkDates,
      tempLightDates: tempLightDates,
      humDarkDates: humDarkDates,
      humDark: humDark,
      humLightDates: humLightDates,
      humLight: humLight,
      tempDarkWater: tempDarkWaterDates,
      tempDarkWaterDates: tempDarkWater,
      tempLightWater: tempLightWaterDates,
      tempLightWaterDates: tempLightWater,
      coolTemp: coolTemp,
    };
  } else {
    // doc.data() will be undefined in this case
    console.log("No such document!");
  }
};

// Use the public catalogue maintained by the dashboard's Fresh Products tab.
export const subscribeFreshProducts = (onProducts, onError, link) =>
  onSnapshot(
    query(
      collection(db, "products_fresh"),
      where("isPublic", "==", true),
      ...(link === undefined ? [] : [where("link", "==", link)])
    ),
    (snapshot) => {
      const products = snapshot.docs.map((document) => {
        const data = document.data();
        const product = { ...data, firebaseId: document.id };
        ["name", "subtitle", "color", "flavor", "texture"].forEach((field) => {
          product[field] = [data[`${field}_sv`] || "", data[`${field}_en`] || ""];
        });
        ["description", "kitchen", "nutrientList"].forEach((field) => {
          const sv = data[`${field}_sv`] || [];
          const en = data[`${field}_en`] || [];
          product[field] = Array.from({ length: Math.max(sv.length, en.length) },
            (_, index) => [sv[index] || "", en[index] || ""]);
        });
        product.urlArr = data.urlArr?.length ? data.urlArr : data.url ? [data.url] : [];
        return product;
      });
      onProducts(products.sort((a, b) => a.name[0].localeCompare(b.name[0], "sv")));
    },
    onError
  );

// Public processed-product copy and localized media come from the dashboard's
// Processade produkter tab. Packages and their prices remain in a private collection.
const mapProcessedProduct = (document) => {
  const data = document.data();
  if (data.isPublic !== true) return null;
  const localized = (field) => [data[`${field}_sv`] || "", data[`${field}_en`] || ""];
  const localizedList = (field) => {
    const sv = Array.isArray(data[`${field}_sv`]) ? data[`${field}_sv`] : [];
    const en = Array.isArray(data[`${field}_en`]) ? data[`${field}_en`] : [];
    return Array.from({ length: Math.max(sv.length, en.length) }, (_, index) => [sv[index] || "", en[index] || ""]);
  };
  const images = ["sv", "en"].map((language) => {
    const gallery = data[`imageGallery_${language}`];
    return [...new Set([data[`mainImage_${language}`], ...(Array.isArray(gallery) ? gallery : [])].filter(Boolean))];
  });
  const nutritionDeclaration = Array.isArray(data.nutritionDeclaration)
    ? data.nutritionDeclaration.filter((row) => row && Number.isFinite(row.value) && row.value >= 0 && row.unit && (row.name_sv || row.name_en))
      .map((row) => ({ name: [row.name_sv || "", row.name_en || ""], value: row.value, unit: row.unit }))
    : [];
  return {
    firebaseId: document.id,
    id: data.id || document.id,
    name: [data.name_sv || "", data.name_en || data.name_sv || ""],
    subtitle: [data.subtitle_sv || "", data.subtitle_en || data.subtitle_sv || ""],
    image: images.map((urls) => urls[0] || ""),
    images,
    method: data.processMethod || (data.productType?.startsWith("dehydrated") ? "dehydrated" : "freeze_dried"),
    flavor: localized("flavor"), color: localized("color"), texture: localized("texture"),
    description: localizedList("description"), kitchen: localizedList("kitchen"),
    nutrientList: localizedList("nutrientList"), allergens: localizedList("allergens"),
    ingredients: localized("ingredients"), shelfLife: localized("shelfLife"), storeData: localized("storeData"),
    storeTemp: Number.isFinite(data.storeTemp) ? data.storeTemp : null,
    nutritionBasis: data.nutritionBasis === "100 ml" ? "100 ml" : "100 g",
    nutritionDeclaration,
  };
};

export const subscribeProcessedProducts = (onProducts, onError, productId) =>
  onSnapshot(
    productId
      ? doc(db, "products_freezedry", productId)
      : query(collection(db, "products_freezedry"), where("isPublic", "==", true)),
    (snapshot) => {
      const documents = productId ? (snapshot.exists() ? [snapshot] : []) : snapshot.docs;
      const products = documents.map(mapProcessedProduct).filter(Boolean);
      onProducts(products.sort((a, b) => a.name[0].localeCompare(b.name[0], "sv")));
    },
    onError
  );

export const getFreshProductPackages = async (parentId) => {
  const snapshot = await getDocs(query(
    collection(db, "products_fresh_package"), where("parentId", "==", parentId)
  ));
  return snapshot.docs.map((document) => document.data())
    .filter((item) => item.active !== false && item.size > 0)
    .sort((a, b) => a.size - b.size);
};

// Customers now contains only public-facing profile fields. Prices, active
// status and other internal details live in customers_private in vert-api.
const publicCustomerCategories = ["store", "retail", "other"];

export const subscribePublicCustomers = (onCustomers, onError) =>
  onSnapshot(
    query(
      collection(db, "customers"),
      where("isPublic", "==", true),
      where("category", "in", publicCustomerCategories)
    ),
    (snapshot) => {
      const customers = snapshot.docs
        .flatMap((document) => {
          const data = document.data();
          if (data.isPublic !== true || !publicCustomerCategories.includes(data.category) ||
              typeof data.name !== "string" || !data.name.trim()) return [];
          return [{
            firebaseId: document.id,
            name: data.name,
            address: data.address || "",
            image: data.image || "",
            url: data.url || "",
            category: data.category,
          }];
        })
        .sort((a, b) => a.name.localeCompare(b.name, "sv"));
      onCustomers(customers);
    },
    onError
  );
